import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType, useNavigate, useParams } from 'react-router-dom';
import { isGroup50EventActive } from './utils/group50Campaign';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { MathJaxProvider } from './components/MathRenderer';
import Navigation from './components/layout/Navigation';
// MigrationNotification 제거
import KakaoBrowserDetect from './components/KakaoBrowserDetect';
import HomePage from './pages/home/HomePage';
import ScrollToTop from './components/ScrollToTop';
import useIsMobile from './hooks/useIsMobile';
// 🚫 이벤트 팝업 제거: 사용자 경험 방해로 인한 제거

// 공지 모달
import AnnouncementModal from './components/announcement/AnnouncementModal';
import WhatsNewModal from './components/announcement/WhatsNewModal';
import { getVisibleAnnouncements } from './config/announcements';

// 랜딩페이지 및 인증 컴포넌트 import
import ResponsiveLandingPage from './pages/ResponsiveLandingPage';
import AuthPage from './pages/AuthPage';
import NotFoundPage from './pages/NotFoundPage';
import ErrorBoundary from './components/common/ErrorBoundary';

// 생기부 AI 컴포넌트 import
import CreateSaenggibu from './pages/saenggibu/CreateSaenggibu';
import SaenggibuHistory from './pages/saenggibu/SaenggibuHistory';
import SaenggibuInspector from './pages/saenggibu/SaenggibuInspector';
import InspectorHistory from './pages/saenggibu/InspectorHistory';
import MyStylePage from './pages/saenggibu/MyStylePage';
import ClassesPage from './pages/classes/ClassesPage';
import ClassStudentsPage from './pages/classes/ClassStudentsPage';
import ChatSettingsPage from './pages/chat/settings/ChatSettingsPage';
import { useChatAppearanceRoot } from './hooks/useChatAppearance';
import BatchSaenggibuPage from './pages/batch-saenggibu/BatchSaenggibuPage';

// 구독 관련 컴포넌트 import
import SubscriptionPage from './pages/subscription/SubscriptionPage';
import ProSubscription from './pages/subscription/ProSubscription';
import PaymentSuccess from './pages/subscription/PaymentSuccess';
import PaymentWidgetSuccess from './pages/payment/PaymentWidgetSuccess';
import PaymentWidgetFail from './pages/payment/PaymentWidgetFail';
import BillingKeyRegistration from './pages/subscription/BillingKeyRegistration';
import BillingSuccess from './pages/subscription/BillingSuccess';
import BillingFail from './pages/subscription/BillingFail';
import PaymentReceipt from './pages/payment/PaymentReceipt';
import RefundReceipt from './pages/subscription/RefundReceipt';
import ServiceConnectionBanner from './components/subscription/ServiceConnectionBanner';

// 🧪 실험실 (Beta) 컴포넌트 import
import LabPage from './pages/lab/LabPage';

// 📊 AI 서술형 채점 페이지
import ScoringPage from './pages/scoring/ScoringPage';
import ScoringHistory from './pages/scoring/ScoringHistory';

// 📋 연간 평가계획서 페이지
import AssessmentPlanPage from './pages/assessment/AssessmentPlanPage';
import AssessmentPlanHistory from './pages/assessment/AssessmentPlanHistory';

// 지원 관련 컴포넌트 import
import SupportPage from './pages/support/SupportPage';
import FaqDetailPage from './pages/support/FaqDetailPage';
import GettingStartedPage from './pages/support/GettingStartedPage';
import PaymentPage from './pages/support/PaymentPage';
import TechnicalPage from './pages/support/TechnicalPage';
import WorksheetPage from './pages/support/WorksheetPage';
import FeaturesPage from './pages/support/FeaturesPage';

// 고객센터 컴포넌트 import (구글 스타일)
import HelpCenterPage from './pages/help/HelpCenterPage';

// 팀 소개 페이지 import
import TeamPage from './pages/team/TeamPage';

// 법정 필수 페이지 import
import CompanyInfo from './pages/CompanyInfo';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import AccountDeletePage from './pages/account/AccountDeletePage';
import InvitePage from './pages/invite/InvitePage';
import InviteWelcomePage from './pages/invite/InviteWelcomePage';
import { canUseInviteFeature } from './api/inviteApi';
import { isPublicInvitePath } from './utils/appRouteAccess';
import { isLibraryDetailTransition } from './utils/libraryRoutes';

// 👨‍💼 관리자 시스템 컴포넌트 import
import AdminHubPage from './pages/admin/AdminHubPage';
import AdminOperationsPage from './pages/admin/AdminOperationsPage';
import InviteOperationsPage from './pages/admin/InviteOperationsPage';
import RefundConsolePage from './pages/admin/RefundConsolePage';
import SchoolTrialReviewPage from './pages/admin/SchoolTrialReviewPage';
import OrganizationAdminPage from './pages/admin/OrganizationAdminPage';
import SchoolAdminApplyPage from './pages/school/SchoolAdminApplyPage';
import JoinOrganizationPage from './pages/school/JoinOrganizationPage';
import SchoolAdminDashboardPage from './pages/school/SchoolAdminDashboardPage';
import BulkPurchaseAdminPage from './pages/admin/BulkPurchaseAdminPage';
import CouponManager from './pages/admin/CouponManager';
import PilotContestManager from './pages/admin/PilotContestManager';
import ContentManager from './pages/admin/ContentManager';
import RoasDashboard from './pages/admin/RoasDashboard';
import IrDashboard from './pages/admin/IrDashboard';
import UsageDashboard from './pages/admin/UsageDashboard';
import RevenueFunnelPage from './pages/admin/RevenueFunnelPage';
import LeadSender from './pages/admin/LeadSender';
import CustomerLeadsPage from './pages/admin/CustomerLeadsPage';
import AdminPricingPolicyPage from './pages/admin/AdminPricingPolicyPage';

// 📊 이벤트 트래킹 시스템
import { initEventTracker } from './utils/eventTracker';

// 블로그 시스템 컴포넌트 import
import BlogPage from './pages/blog/BlogPage';
import BlogPostPage from './pages/blog/BlogPostPage';

// 자동 발행 블로그 (Articles)
import ArticlesPage from './pages/articles/ArticlesPage';
import ArticleDetailPage from './pages/articles/ArticleDetailPage';
import NewsListPage from './pages/news/NewsListPage';
import NewsDetailPage from './pages/news/NewsDetailPage';
import NewsSubscriptionActionPage from './pages/news/NewsSubscriptionActionPage';

// 기능 랜딩페이지 import (SEO)
import AiPptLandingPage from './pages/features/AiPptLandingPage';
import AiWorksheetLandingPage from './pages/features/AiWorksheetLandingPage';
import AiImageLandingPage from './pages/features/AiImageLandingPage';

// 경쟁사 비교 페이지 import
import ChatGPTCompare from './pages/compare/ChatGPTCompare';
import MiricanvasCompare from './pages/compare/MiricanvasCompare';
import MyaieduCompare from './pages/compare/MyaieduCompare';

// 📚 가이드 페이지 import (SEO 콘텐츠)
import EdzipGuidePage from './pages/guide/EdzipGuidePage';
import LearningSoftwareGuidePage from './pages/guide/LearningSoftwareGuidePage';
import Saenggibu2026GuidePage from './pages/guide/Saenggibu2026GuidePage';
import SetakExampleGuidePage from './pages/guide/SetakExampleGuidePage';
import HakpokGijaeGuidePage from './pages/guide/HakpokGijaeGuidePage';
import TongsinmunGuidePage from './pages/guide/TongsinmunGuidePage';
import HaengbalGuidePage from './pages/guide/HaengbalGuidePage';
import RubricGuidePage from './pages/guide/RubricGuidePage';
import DokseoGuidePage from './pages/guide/DokseoGuidePage';
import SundoGuidePage from './pages/guide/SundoGuidePage';
import AiPptGuidePage from './pages/guide/AiPptGuidePage';
import AiWorksheetGuidePage from './pages/guide/AiWorksheetGuidePage';
import AiImageGuidePage from './pages/guide/AiImageGuidePage';
import AiTeacherToolsGuidePage from './pages/guide/AiTeacherToolsGuidePage';

// 커뮤니티 시스템 컴포넌트 import
import CommunityPage from './pages/community/CommunityPage';
import CommunityLandingPage from './pages/community/CommunityLandingPage';
import PostDetailPage from './pages/community/PostDetailPage';

// 라이브러리 (구 나눔터/갤러리) 컴포넌트 import
import LibraryPage from './pages/gallery/GalleryPage';
import LibraryLandingPage from './pages/library/LibraryLandingPage';

// 📢 광고 랜딩페이지 import
import SchoolTrialLandingPage from './pages/landing/SchoolTrialLandingPage';
import AdLandingPage from './pages/landing/ad-landing/AdLandingPage';
import WelcomeLandingPage from './pages/welcome/WelcomeLandingPage';

// 🎪 이벤트 시스템 컴포넌트 import
import EventsMainPage from './pages/events/EventsMainPage';
import BlogEventPage from './pages/events/BlogEventPage';
import CommunityEventPage from './pages/events/CommunityEventPage';
import InterviewEventPage from './pages/events/InterviewEventPage';
import ReferralEventPage from './pages/events/ReferralEventPage';
import ShareEventPage from './pages/events/ShareEventPage';
import StudentProfileEvent from './pages/events/student-profile/StudentProfileEvent';
import SchoolChallengeEventPage from './pages/events/SchoolChallengeEventPage';
import ClassPhotoEventPage from './pages/events/ClassPhotoEventPage';
import ClassActivityEventPage from './pages/events/ClassActivityEventPage';
import ClassPlayEventPage from './pages/events/ClassPlayEventPage';
import ExhibitionRegistrationPage from './pages/events/ExhibitionRegistrationPage';
import AmbassadorEventPage from './pages/events/AmbassadorEventPage';

// 💎 요금제 시스템 컴포넌트 import
import PricingPage from './pages/pricing/PricingPage';
import BulkPurchasePage from './pages/purchase/BulkPurchasePage';
import BulkPurchaseSuccessPage from './pages/purchase/BulkPurchaseSuccessPage';
import BulkPurchaseFailPage from './pages/purchase/BulkPurchaseFailPage';
import BulkPurchaseRefundPage from './pages/purchase/BulkPurchaseRefundPage';

// 🔗 대화 공유 페이지
import SharedChatPage from './pages/share/SharedChatPage';

// 인증 관련 컴포넌트 및 컨텍스트 import
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { WorksheetProvider } from './contexts/WorksheetContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import PrivateRoute from './Auth/PrivateRoute';
import Profile from './Auth/Profile';

// 토스트 시스템 (글로벌)
import { ToastProvider } from './components/Toast';
// 레이어 등록부 (다이얼로그·바텀시트가 열려 있으면 토스트가 기다린다) + 라우트 전환 시 토스트 정리
import { LayerProvider } from './components/common/LayerContext';
import RouteToastCleaner from './components/common/RouteToastCleaner';
// 확인 대화상자 (글로벌, 브라우저 기본 확인창 대체) — useConfirm()
import { ConfirmDialogProvider } from './components/common/ConfirmDialog';

// 7일 무료 체험 자동 트리거 (회원가입 직후 pending 키가 있으면 자동 시작)
import FreeTrialAutoStarter from './components/cta/FreeTrialAutoStarter';
import Ai30ArrivalTracker from './components/cta/Ai30ArrivalTracker';
import ClassPhotoTrialWelcome from './components/events/ClassPhotoTrialWelcome';
import InviteAttributionBridge from './components/invite/InviteAttributionBridge';

// 📝 학생 과제 제출 페이지 (공개, 동적 로딩)
const ChatPage = React.lazy(() => import('./pages/chat/ChatPage'));
const SchoolReviewListPage = React.lazy(() => import('./pages/school/review/SchoolReviewListPage'));
const SchoolReviewTryPage = React.lazy(() => import('./pages/enterprise/SchoolReviewTryPage'));
const SchoolReviewPage = React.lazy(() => import('./pages/school/review/SchoolReviewPage'));
const MySchoolReviewPage = React.lazy(() => import('./pages/school/review/MySchoolReviewPage'));
const ScopeReviewPage = React.lazy(() => import('./pages/school/review/ScopeReviewPage'));
const SecondSemesterAiEventPage = React.lazy(
  () => import('./pages/events/SecondSemesterAiEventPage'),
);
const YearEndFestaEventPage = React.lazy(
  () => import('./pages/events/YearEndFestaEventPage'),
);
const Group50EventPage = React.lazy(
  () => import('./pages/events/Group50EventPage'),
);
const Pro50EventPage = React.lazy(
  () => import('./pages/events/Pro50EventPage'),
);
const ProAiEventPage = React.lazy(
  () => import('./pages/events/ProAiEventPage'),
);
const ScoringEventPage = React.lazy(
  () => import('./pages/events/ScoringEventPage'),
);
const HwpDocEventPage = React.lazy(
  () => import('./pages/events/HwpDocEventPage'),
);
// 한글 파일 이벤트 v2: 유형별 URL 3개(시험지·안내문·생기부) + /lp 별칭. 페이지는 하나, type prop만 다르다.
const HWP_DOC_EVENT_ROUTES = [
  ['/events/hwp-exam', 'exam'],
  ['/lp/hwp-exam', 'exam'],
  ['/events/hwp-notice', 'notice'],
  ['/lp/hwp-notice', 'notice'],
  ['/events/hwp-record', 'record'],
  ['/lp/hwp-record', 'record'],
];
const HwpDocBootFallback = () => (
  <div className="hwp-doc-boot" style={{ minHeight: '100vh', background: '#fff' }} />
);
const StudentSubmitPage = React.lazy(() => import('./pages/submit/StudentSubmitPage'));
const RiseAdminDelivery = React.lazy(() => import('./pages/admin/RiseAdminDelivery'));
const RiseAdminSurvey = React.lazy(() => import('./pages/admin/RiseAdminSurvey'));
const RiseAdminCoupon = React.lazy(() => import('./pages/admin/RiseAdminCoupon'));
const RiseAdminUsers = React.lazy(() => import('./pages/admin/RiseAdminUsers'));

// 🏢 온종일돌봄시설 평가 시스템 컴포넌트 (동적 로딩 - 성능 최적화)
const DaycareEvaluationAccess = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationAccess'));
const DaycareEvaluationDashboard = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationDashboard'));
const DaycareEvaluationSelfForm = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationSelfForm'));
const DaycareEvaluationAdmin = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationAdmin'));
const DaycareEvaluationEmployeeAccess = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationEmployeeAccess'));
const DaycareEvaluationSupervisorForm = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationSupervisorForm'));
const DaycareEvaluationResult = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationResult'));
const DaycareEvaluationTemplateEdit = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationTemplateEdit'));
const DaycareEvaluationMultiForm = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationMultiForm'));
const DaycareEvaluationMultiList = React.lazy(() => import('./pages/private/daycare-evaluation/DaycareEvaluationMultiList'));
const WatkidsEvalApp = React.lazy(() => import('./pages/private/watkids-eval/WatkidsEvalApp'));

// 🎓 RISE 교원대 실증 파일럿 (동적 로딩)
const RisePilotLanding = React.lazy(() => import('./pages/pilot/rise/RisePilotLanding'));
const RisePilotSurvey = React.lazy(() => import('./pages/pilot/rise/RisePilotSurvey'));
const RisePilotComplete = React.lazy(() => import('./pages/pilot/rise/RisePilotComplete'));
const RisePilotCompleteDone = React.lazy(() => import('./pages/pilot/rise/RisePilotCompleteDone'));
const RisePilotTimeReceipt = React.lazy(() => import('./pages/pilot/rise/RisePilotTimeReceipt'));
const RisePilotDashboard = React.lazy(() => import('./pages/pilot/rise/RisePilotDashboard'));
const RisePilotPresentation = React.lazy(() => import('./pages/pilot/rise/RisePilotPresentation'));
const RisePilotWhitePaper = React.lazy(() => import('./pages/pilot/rise/RisePilotWhitePaper'));
const RisePilotContest = React.lazy(() => import('./pages/pilot/rise/RisePilotContest'));
const RisePilotContestGallery = React.lazy(() => import('./pages/pilot/rise/RisePilotContestGallery'));
const RisePilotContestSubmit = React.lazy(() => import('./pages/pilot/rise/RisePilotContestSubmit'));

// 연수 파트너 안내·조건 확인·투명 정산 (사용 빈도가 낮아 동적 로딩)
const PartnerProgramPage = React.lazy(() => import('./pages/partner/PartnerProgramPage'));
const PartnerPresentationPage = React.lazy(() => import('./pages/partner/PartnerPresentationPage'));
const PartnerAgreementPage = React.lazy(() => import('./pages/partner/PartnerAgreementPage'));
const PartnerDashboardPage = React.lazy(() => import('./pages/partner/PartnerDashboardPage'));
const PartnerContractDraftPage = React.lazy(() => import('./pages/partner/PartnerContractDraftPage'));
const TrainingAcademyPage = React.lazy(() => import('./pages/training/TrainingAcademyPage'));
const TrainingLandingPage = React.lazy(() => import('./pages/landing/TrainingLandingPage'));
const TrainingCourseDetailPage = React.lazy(() => import('./pages/training/TrainingCourseDetailPage'));
const TrainingCheckoutPage = React.lazy(() => import('./pages/training/TrainingCheckoutPage'));
const TrainingPaymentSuccessPage = React.lazy(() => import('./pages/training/TrainingPaymentSuccessPage'));
const TrainingPaymentFailPage = React.lazy(() => import('./pages/training/TrainingPaymentFailPage'));
const TrainingLearnPage = React.lazy(() => import('./pages/training/TrainingLearnPage'));
const MyTrainingsPage = React.lazy(() => import('./pages/training/MyTrainingsPage'));
const TrainingEvidencePage = React.lazy(() => import('./pages/training/TrainingEvidencePage'));
const PaymentDocumentsPage = React.lazy(() => import('./pages/subscription/PaymentDocumentsPage'));
const TrainingAdminPage = React.lazy(() => import('./pages/admin/TrainingAdminPage'));
const TrainingOrdersAdminPage = React.lazy(() => import('./pages/admin/TrainingOrdersAdminPage'));
const PartnerSettlementAdminPage = React.lazy(() => import('./pages/admin/PartnerSettlementAdminPage'));
const TrainingOperationsGuidePage = React.lazy(() => import('./pages/admin/TrainingOperationsGuidePage'));

const PartnerPageFallback = () => (
  <div
    aria-label="파트너 페이지를 불러오는 중"
    style={{
      minHeight: '100vh',
      background: '#fff',
      padding: '88px 20px',
    }}
  >
    <div style={{ maxWidth: '760px', margin: '0 auto' }}>
      {[220, 520, 520].map((width, index) => (
        <div
          key={width + index}
          style={{
            width: index === 0 ? `${width}px` : '100%',
            maxWidth: '100%',
            height: index === 0 ? '40px' : '120px',
            marginBottom: '18px',
            borderRadius: '16px',
            background: '#f2f4f6',
          }}
        />
      ))}
    </div>
  </div>
);

// 9월 단체 50% 랜딩은 9/30에 끝난다 — 이후 광고·공유 링크는 쿼리(UTM 등)를 그대로 들고 10월 랜딩으로 간다.
const Group50Route = ({ children }) => {
  const location = useLocation();
  if (!isGroup50EventActive()) {
    return <Navigate to={`/events/pro-50${location.search}`} replace />;
  }
  return children;
};

// 옛 /deals/:promoCode 특가 링크는 쿼리(UTM 등)를 그대로 들고 요금제로 간다.
const DealsRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/pricing${location.search}`} replace />;
};

const GalleryRedirect = () => {
  const { itemId } = useParams();
  return <Navigate to={`/library/${itemId}`} replace />;
};

const InviteFeatureRoute = () => {
  const { currentUser } = useAuth();
  if (!canUseInviteFeature(currentUser)) {
    return <Navigate to="/home" replace />;
  }
  return (
    <PrivateRoute>
      <InvitePage />
    </PrivateRoute>
  );
};

const RouteChangeListener = ({ children }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const isMobile = useIsMobile();
  
  // 페이지 이동 시 네비게이션 메뉴 닫기
  React.useEffect(() => {
    const closeMenu = () => {
      if (isMobile) {
        const menuTriggerElement = document.querySelector('[data-mobile-menu]');
        if (menuTriggerElement && menuTriggerElement.__closeMenu) {
          menuTriggerElement.__closeMenu();
        }
      }
    };
    
    closeMenu();
  }, [isMobile, location.pathname, navigationType]);
  
  return <>{children}</>;
};

// 맥락 인지 페이지 전환 애니메이션 (PUSH → 오른쪽에서, POP → 왼쪽에서, REPLACE → 페이드)
// 주의: willChange/transform를 유지하면 자식의 position:fixed가 깨지므로 애니메이션 완료 후 반드시 제거
const PageTransition = ({ children }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const containerRef = React.useRef(null);
  const prevPathnameRef = React.useRef(location.pathname);
  const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;
    if (!el) return;
    // 라이브러리 자료 상세는 목록 위에 뜨는 창이라 /library ↔ /library/:id 이동에서 목록을 흔들지 않는다.
    if (isLibraryDetailTransition(prevPathname, location.pathname)) return;

    el.style.animation = 'none';
    void el.offsetHeight;

    const duration = navigationType === 'REPLACE' ? 180 : 220;

    if (navigationType === 'POP') {
      el.style.animation = `page-slide-back ${duration}ms ${easing}`;
    } else if (navigationType === 'PUSH') {
      el.style.animation = `page-slide-forward ${duration}ms ${easing}`;
    } else {
      el.style.animation = `page-fade-in ${duration}ms ${easing}`;
    }

    const timer = setTimeout(() => {
      el.style.animation = '';
    }, duration + 50);

    return () => clearTimeout(timer);
  }, [location.pathname, navigationType]);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
};

// 애플리케이션 레이아웃 컴포넌트
const AppLayout = () => {
  const isMobile = useIsMobile();
  const isPcMode = !isMobile;
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useChatAppearanceRoot(location.pathname);
  
  // 📊 이벤트 트래킹 시스템 초기화 (앱 시작 시 1회)
  React.useEffect(() => {
    initEventTracker();
  }, []);
  
  // 🚫 이벤트 팝업 제거: 사용자 경험 방해로 인한 제거


  
  // 네비게이션을 숨길 경로들 (랜딩페이지, 인증페이지, 온종일돌봄 시스템 등)
  const hideNavigationPaths = [
    '/auth',
    '/ad/pro/apply',
    '/events/exhibition',
    '/events/class-photo',
    '/lp/class-photo',
    '/events/class-activity',
    '/lp/class-activity',
    '/events/class-play',
    '/lp/class-play',
    '/events/2nd-semester-ai',
    '/events/year-end-festa',
    '/events/group-50',
    '/events/pro-50',
    '/events/pro-ai',
    '/events/scoring',
    '/events/hwp',
    '/events/hwp-exam',
    '/events/hwp-notice',
    '/events/hwp-record',
    '/lp/hwp-exam',
    '/lp/hwp-notice',
    '/lp/hwp-record',
  ];
  const isDaycareEvaluationPath = location.pathname.startsWith('/private/daycare-evaluation')
    || location.pathname.startsWith('/private/watkids-eval');
  const isStudentSubmitPath = location.pathname.startsWith('/submit/');
  const isRisePilotPath = location.pathname.startsWith('/pilot/rise');
  const isPartnerPath = location.pathname.startsWith('/partner');
  const isTrainingFocusPath = (
    location.pathname.includes('/checkout')
    || location.pathname.endsWith('/learn')
    || location.pathname.startsWith('/training/payment')
  );
  const shouldShowNavigation = !hideNavigationPaths.includes(location.pathname) && !isDaycareEvaluationPath && !isStudentSubmitPath && !isRisePilotPath && !isPartnerPath && !isTrainingFocusPath;

  // 도메인 리다이렉트 안전장치 (React 레벨에서 2차 체크)
  React.useEffect(() => {
    const currentHostname = window.location.hostname;
    
    // Firebase 기본 도메인들에서 메인 도메인으로 리다이렉트
    if (currentHostname === 'whatquiz-bd17e.web.app' || currentHostname === 'whatquiz-bd17e.firebaseapp.com') {
      const targetUrl = 'https://whatquiz.co.kr' + window.location.pathname + window.location.search + window.location.hash;

      window.location.replace(targetUrl);
    }
  }, []);

  // 전역 인증 상태 체크 및 리다이렉트
  React.useEffect(() => {
    // 🆕 인증 없이 접근 가능한 공개 경로들 (확장)
    const publicPaths = [
      '/',
      '/auth',
      // 🆕 새로 추가된 공개 페이지들
      '/home',                        // 홈페이지 공개
      '/create-free-worksheet',       // 자유형 학습지 생성 공개
      '/create-saenggibu',            // 생기부 작성 공개
      '/saenggibu-selection',         // 옛 생기부 선택 주소 → /create-saenggibu 로 이동
      '/saenggibu-inspector',         // 생기부 검사기 공개
      '/saenggibu-my-style',          // 내 스타일 설정 공개 (로그인 유도 퍼널)
      '/scoring',                     // 채점 페이지 공개
      '/assessment-plan',             // 평가계획서 페이지 공개
      '/pricing',                     // 요금제 페이지 공개
      '/enterprise/school-review',    // 학교 생기부 검토 체험(가상 데이터) 공개
      '/bulk-purchase',               // 대량 구매(비회원 카드 결제) 공개
      '/bulk-purchase/success',       // 대량 구매 결제 완료 화면 공개
      '/bulk-purchase/fail',          // 대량 구매 결제 실패 화면 공개
      '/bulk-purchase/refund',        // 대량 구매 환불 요청 폼 공개
      '/chat',                        // AI 교사 채팅 공개
      '/chat/settings',               // AI 맞춤 설정 허브 (페이지 안에서 로그인 유도)
      // 기존 공개 페이지들
      '/team',
      '/company-info',
      '/privacy-policy', 
      '/terms-of-service',
      '/refund-policy',
      '/account/delete',             // 앱스토어 계정 삭제 정책 URL 공개
      '/login',
      '/register',
      '/reset-password',
      // 지원 관련 페이지들 (공개 접근)
      '/support',
      '/support/faq',
      '/support/getting-started',
      '/support/worksheet', 
      '/support/features',
      '/support/payment',
      '/support/technical',
      // 고객센터 (공개 접근)
      '/help',
      // 블로그 관련 페이지들 (공개 접근)
      '/blog',
      // 🎓 RISE 파일럿 (공개 접근)
      '/pilot/rise',
      '/pilot/rise/survey',
      '/pilot/rise/complete',
      '/pilot/rise/complete-done',
      '/pilot/rise/time-receipt',
      '/pilot/rise/dashboard',
      '/pilot/rise/presentation',
      '/pilot/rise/white-paper',
      // 연수 파트너 공개 안내·미팅 프레젠테이션
      '/partner',
      '/partner/present',
      '/partner/contract',
      '/partner/contract-draft',
      '/training',
      '/training/visit',
      '/training/payment/fail'
    ];

    // 🆕 진짜 보호가 필요한 페이지들만 명시
    const trulyProtectedPaths = [
      '/subscription/billing-key',
      '/subscription/documents',
      '/billing/success',
      '/billing/fail', 
      '/payment/receipt',
      '/payment/success',
      '/payment/widget-success',
      '/payment/widget-fail',
      '/refund/receipt',
      '/profile',
      '/classes',
      '/partner/agree',
      '/partner/dashboard',
      '/my/trainings',
      '/training/payment/success',
      ...(canUseInviteFeature(currentUser) ? ['/invite'] : []),
      '/my-free-worksheets', 
      '/my-performance-assessments',
      '/admin'
    ];

    // 로딩 중인 경우 대기
    if (loading) {
      return;
    }

    // 로그인된 사용자가 랜딩페이지에 접근하는 경우 /home으로 리다이렉트
    if (currentUser && location.pathname === '/') {
      navigate(`/home${location.search || ''}`, { replace: true });
      return;
    }

    // 🆕 공개 경로 체크 (동적 경로 포함)
    const isPublicTrainingDetail = /^\/training\/[a-z0-9-]+$/.test(
      location.pathname,
    );
    const isPublicPath = publicPaths.includes(location.pathname) ||
                        isPublicInvitePath(location.pathname) ||
                        location.pathname.startsWith('/support/faq/') ||
                        location.pathname.startsWith('/blog/') ||
                        location.pathname.startsWith('/help/') ||
                        isPublicTrainingDetail;
    
    if (isPublicPath) {
      return;
    }

    // 🆕 보호된 페이지만 인증 체크 (더 정밀한 제어)
    const isProtectedPath = trulyProtectedPaths.some(path => 
      location.pathname === path || location.pathname.startsWith(path + '/')
    ) || (
      location.pathname.startsWith('/training/')
      && (
        location.pathname.endsWith('/checkout')
        || location.pathname.endsWith('/learn')
      )
    );

    if (isProtectedPath && !currentUser) {
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      navigate(`/auth?redirect=${returnUrl}`, { replace: true });
    }
  }, [currentUser, loading, location.pathname, location.search, navigate]);

  return (
    <div className="app-container">
      {/* 마이그레이션 알림 제거 */}
      {shouldShowNavigation && <Navigation />}
      {shouldShowNavigation && <ServiceConnectionBanner />}
      
      {/* 🚫 이벤트 팝업 제거됨 */}
      

      
      <div className="content-wrapper" style={{ 
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        overflowX: 'hidden',
        paddingLeft: shouldShowNavigation && isPcMode ? 'var(--whatquiz-nav-width, 240px)' : '0',
        transition: 'padding-left 0.3s ease'
      }}>
        <PageTransition>
        <ErrorBoundary>
        <Routes>
          {/* 반응형 랜딩 페이지 (디바이스별 자동 선택) */}
          <Route path="/" element={<ResponsiveLandingPage />} />
          
          {/* 🆕 홈페이지 (공개 접근) */}
          <Route path="/home" element={<HomePage />} />
          
          {/* 📝 학생 과제 제출 (공개, 인증 불필요) */}
          <Route path="/submit/:code" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f7f8fa',
                color: '#8b95a1',
                fontSize: '14px',
              }}>
                불러오는 중...
              </div>
            }>
              <StudentSubmitPage />
            </React.Suspense>
          } />

          {/* 인증 페이지 */}
          <Route path="/auth" element={<AuthPage />} />
          
          {/* 2026 노원구온종일돌봄사업단 근무성적평정 (왓키즈AI) */}
          <Route path="/private/watkids-eval/*" element={
            <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#f2f4f6' }} />}>
              <WatkidsEvalApp />
            </React.Suspense>
          } />
          {/* 🏢 온종일돌봄시설 근무성적평정 시스템 (완전 독립적, 동적 로딩) */}
          <Route path="/private/daycare-evaluation/access" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                불러오고 있어요
              </div>
            }>
              <DaycareEvaluationAccess />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/dashboard" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                불러오고 있어요
              </div>
            }>
              <DaycareEvaluationDashboard />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/self-evaluation" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                불러오고 있어요
              </div>
            }>
              <DaycareEvaluationSelfForm />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/admin" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                관리자 시스템 로딩 중...
              </div>
            }>
              <DaycareEvaluationAdmin />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/employee/:urlToken" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                개인 링크 인증 중...
              </div>
            }>
              <DaycareEvaluationEmployeeAccess />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/supervisor-evaluation/:employeeId" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                상급자 평가 폼 로딩 중...
              </div>
            }>
              <DaycareEvaluationSupervisorForm />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/result/:employeeId" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                평가 결과 로딩 중...
              </div>
            }>
              <DaycareEvaluationResult />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/template-edit/:position" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                평가 양식 로딩 중...
              </div>
            }>
              <DaycareEvaluationTemplateEdit />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/multi-evaluation/:employeeId" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                다면평가 폼 로딩 중...
              </div>
            }>
              <DaycareEvaluationMultiForm />
            </React.Suspense>
          } />
          <Route path="/private/daycare-evaluation/multi-evaluation-list" element={
            <React.Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                다면평가 목록 로딩 중...
              </div>
            }>
              <DaycareEvaluationMultiList />
            </React.Suspense>
          } />

          {/* 🎓 RISE 교원대 실증 파일럿 (공개, 독립 페이지) */}
          <Route path="/pilot/rise" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fff', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotLanding />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/survey" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fff', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotSurvey />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/complete" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fff', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotComplete />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/complete-done" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fff', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotCompleteDone />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/time-receipt" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#F9FAFB', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotTimeReceipt />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/dashboard" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#F9FAFB', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotDashboard />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/presentation" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0F172A', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotPresentation />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/white-paper" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fff', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotWhitePaper />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/contest" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fff', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotContest />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/contest/gallery" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#F9FAFB', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotContestGallery />
            </React.Suspense>
          } />
          <Route path="/pilot/rise/contest/submit" element={
            <React.Suspense fallback={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#ffffff', color: '#6B7280', fontSize: '16px', fontWeight: '600' }}>
                로딩 중...
              </div>
            }>
              <RisePilotContestSubmit />
            </React.Suspense>
          } />
          
          {/* 🆕 생성 페이지들 (공개 접근, 기능 사용 시 로그인 필요) */}
          <Route path="/create-saenggibu" element={<CreateSaenggibu />} />
          <Route path="/saenggibu-selection" element={<Navigate to="/create-saenggibu" replace />} />
          <Route path="/saenggibu-history" element={<SaenggibuHistory />} />
          <Route path="/saenggibu-inspector" element={<SaenggibuInspector />} />
          <Route path="/inspector-history" element={<InspectorHistory />} />
          <Route path="/batch-saenggibu" element={<BatchSaenggibuPage />} />
          <Route path="/saenggibu-my-style" element={<MyStylePage />} />
          <Route path="/classes" element={<ClassesPage />} />
          <Route path="/classes/:rosterId" element={<ClassStudentsPage />} />
          <Route path="/classes/:rosterId/students/:sid" element={<ClassStudentsPage />} />
          
          {/* 💎 요금제 페이지 (공개 접근) */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/enterprise/school-review" element={<SchoolReviewTryPage />} />
          <Route path="/bulk-purchase" element={<BulkPurchasePage />} />
          <Route path="/join/:token" element={<JoinOrganizationPage />} />
          <Route path="/school-admin" element={
            <PrivateRoute>
              <SchoolAdminDashboardPage />
            </PrivateRoute>
          } />
          <Route path="/school-admin/review" element={
            <PrivateRoute>
              <SchoolReviewListPage />
            </PrivateRoute>
          } />
          <Route path="/school-admin/review/:reviewId" element={
            <PrivateRoute>
              <SchoolReviewPage />
            </PrivateRoute>
          } />
          <Route path="/school-review" element={
            <PrivateRoute>
              <MySchoolReviewPage />
            </PrivateRoute>
          } />
          <Route path="/school-review/:reviewId" element={
            <PrivateRoute>
              <MySchoolReviewPage />
            </PrivateRoute>
          } />
          <Route path="/school-review/:reviewId/together" element={
            <PrivateRoute>
              <ScopeReviewPage />
            </PrivateRoute>
          } />
          <Route path="/school-admin/apply" element={
            <PrivateRoute>
              <SchoolAdminApplyPage />
            </PrivateRoute>
          } />
          <Route path="/bulk-purchase/success" element={<BulkPurchaseSuccessPage />} />
          <Route path="/bulk-purchase/fail" element={<BulkPurchaseFailPage />} />
          <Route path="/bulk-purchase/refund" element={<BulkPurchaseRefundPage />} />
          
          {/* ⚙️ AI 맞춤 설정 허브 (선생님 정보·AI 기억·내 프롬프트; 비로그인은 페이지 안에서 로그인 안내) */}
          <Route path="/chat/settings" element={<ChatSettingsPage />} />

          {/* 🆕 채팅 페이지 (공개 접근, 메시지 전송 시 로그인 필요) */}
          <Route path="/chat" element={
            <React.Suspense fallback={
              <main
                aria-live="polite"
                role="status"
                style={{
                  minHeight: '100dvh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--wq-dk-bg, #ffffff)',
                  color: 'var(--wq-dk-text-3, #6b7280)',
                  fontSize: '15px',
                }}
              >
                AI 채팅을 준비하고 있어요
              </main>
            }>
              <ChatPage />
            </React.Suspense>
          } />
          
          {/* 🔗 대화 공유 페이지 (공개 접근) */}
          <Route path="/share/:shareId" element={<SharedChatPage />} />
          
          {/* 📊 AI 서술형 채점 */}
          <Route path="/scoring" element={<ScoringPage />} />
          <Route path="/scoring-history" element={<ScoringHistory />} />
          
          {/* 📋 연간 평가계획서 */}
          <Route path="/assessment-plan" element={<AssessmentPlanPage />} />
          <Route path="/assessment-plan-history" element={<AssessmentPlanHistory />} />
          
          {/* 🧪 실험실 (Beta) */}
          <Route path="/lab" element={<LabPage key="lab-main" />} />
          <Route path="/lab/image" element={<Navigate to="/chat?category=image_generation" replace />} />
          {/* 옛 학습지 화면 주소 — 검색·블로그·북마크로 계속 들어온다 */}
          <Route path="/create-worksheet" element={<Navigate to="/chat?category=worksheet_gen" replace />} />
          <Route path="/create-free-worksheet" element={<Navigate to="/chat?category=worksheet_gen" replace />} />
          <Route path="/lab/evaluation" element={<ScoringPage />} /> {/* 기존 URL 호환 */}
          
          {/* 🆕 블로그 페이지들 (공개 접근) */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:postId" element={<BlogPostPage />} />
          
          {/* 📰 자동 발행 블로그 (Articles) */}
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:articleId" element={<ArticleDetailPage />} />

          {/* 🗞️ 매일 아침 교육 뉴스 */}
          <Route path="/news" element={<NewsListPage />} />
          <Route path="/news/confirm" element={<NewsSubscriptionActionPage action="confirm" />} />
          <Route path="/news/unsubscribe" element={<NewsSubscriptionActionPage action="unsubscribe" />} />
          <Route path="/news/:day" element={<NewsDetailPage />} />
          
          {/* 🚀 기능 랜딩페이지 (SEO) */}
          <Route path="/ai-ppt" element={<AiPptLandingPage />} />
          <Route path="/ai-worksheet" element={<AiWorksheetLandingPage />} />
          <Route path="/ai-image" element={<AiImageLandingPage />} />

          {/* 🆚 경쟁사 비교 페이지들 */}
          <Route path="/compare/chatgpt" element={<ChatGPTCompare />} />
          <Route path="/compare/miricanvas" element={<MiricanvasCompare />} />
          <Route path="/compare/myaiedu" element={<MyaieduCompare />} />
          
          {/* 📚 가이드 페이지들 (SEO 콘텐츠) */}
          <Route path="/guide/edzip" element={<EdzipGuidePage />} />
          <Route path="/guide/learning-software" element={<LearningSoftwareGuidePage />} />
          <Route path="/guide/saenggibu-2026" element={<Saenggibu2026GuidePage />} />
          <Route path="/guide/setak-example" element={<SetakExampleGuidePage />} />
          <Route path="/guide/hakpok-gijae" element={<HakpokGijaeGuidePage />} />
          <Route path="/guide/tongsinmun" element={<TongsinmunGuidePage />} />
          <Route path="/guide/haengbal" element={<HaengbalGuidePage />} />
          <Route path="/guide/rubric" element={<RubricGuidePage />} />
          <Route path="/guide/dokseo" element={<DokseoGuidePage />} />
          <Route path="/guide/sundo" element={<SundoGuidePage />} />
          <Route path="/guide/ai-ppt" element={<AiPptGuidePage />} />
          <Route path="/guide/ai-worksheet" element={<AiWorksheetGuidePage />} />
          <Route path="/guide/ai-image" element={<AiImageGuidePage />} />
          <Route path="/guide/ai-teacher-tools" element={<AiTeacherToolsGuidePage />} />
          
          {/* 📚 라이브러리 (공개 열람, 공유/좋아요는 로그인 필요) */}
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/library/:itemId" element={<LibraryPage />} />

          {/* 📚 라이브러리 SEO 랜딩 */}
          <Route path="/library/about" element={<LibraryLandingPage />} />

          {/* 📂 구 나눔터 URL 리다이렉트 */}
          <Route path="/gallery" element={<Navigate to="/library" replace />} />
          <Route path="/gallery/:itemId" element={<GalleryRedirect />} />

          {/* 👥 커뮤니티 SEO 랜딩 (공개 접근) */}
          <Route path="/community/about" element={<CommunityLandingPage />} />

          {/* 👥 커뮤니티 페이지들 (로그인 필수) */}
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/community/posts/:postId" element={<PostDetailPage />} />
          
          {/* 🎁 쿠폰 등록 전용 랜딩 (공개 접근) */}
          <Route path="/welcome" element={<WelcomeLandingPage />} />

          {/* 📢 광고 랜딩페이지 (공개 접근, 로그인 불필요) */}
          <Route path="/pre-register" element={<Navigate to={`/pricing${window.location.search}`} replace />} />
          <Route path="/training" element={
            <React.Suspense fallback={<PartnerPageFallback />}>
              <TrainingAcademyPage />
            </React.Suspense>
          } />
          <Route path="/training/visit" element={
            <React.Suspense fallback={<PartnerPageFallback />}>
              <TrainingLandingPage />
            </React.Suspense>
          } />
          <Route path="/training/payment/fail" element={
            <React.Suspense fallback={<PartnerPageFallback />}>
              <TrainingPaymentFailPage />
            </React.Suspense>
          } />
          <Route path="/training/payment/success" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <TrainingPaymentSuccessPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/training/:slug/checkout" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <TrainingCheckoutPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/training/:slug/learn" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <TrainingLearnPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/training/:slug" element={
            <React.Suspense fallback={<PartnerPageFallback />}>
              <TrainingCourseDetailPage />
            </React.Suspense>
          } />
          <Route path="/school" element={<SchoolTrialLandingPage />} />
          <Route path="/deals/:promoCode" element={<DealsRedirect />} />
          <Route path="/lp/class-photo" element={<ClassPhotoEventPage />} />
          <Route path="/lp/class-activity" element={<ClassActivityEventPage />} />
          <Route path="/lp/class-play" element={<ClassPlayEventPage />} />
          <Route path="/lp/:adSlug" element={<AdLandingPage />} />
          <Route path="/ad/pro" element={<Navigate to={`/pricing${window.location.search}`} replace />} />
          <Route path="/ad/pro/apply" element={<Navigate to={`/pricing${window.location.search}`} replace />} />

          {/* 🎪 이벤트 페이지들 (공개 접근) */}
          <Route path="/events" element={<EventsMainPage />} />
          <Route path="/events/blog" element={<BlogEventPage />} />
          <Route path="/events/community" element={<CommunityEventPage />} />
          <Route path="/events/interview" element={<InterviewEventPage />} />
          <Route path="/events/referral" element={<ReferralEventPage />} />
          <Route path="/events/share" element={<ShareEventPage />} />
          <Route path="/events/profile" element={<StudentProfileEvent />} />
          <Route path="/events/school-challenge" element={<SchoolChallengeEventPage />} />
          <Route path="/events/class-photo" element={<ClassPhotoEventPage />} />
          <Route path="/events/class-activity" element={<ClassActivityEventPage />} />
          <Route path="/events/class-play" element={<ClassPlayEventPage />} />
          <Route path="/events/exhibition" element={<ExhibitionRegistrationPage />} />
          <Route path="/events/ambassador" element={<AmbassadorEventPage />} />
          <Route path="/events/2nd-semester-ai" element={
            <React.Suspense fallback={<div className="ai30-boot" style={{ minHeight: '100vh', background: '#fff' }} />}>
              <SecondSemesterAiEventPage />
            </React.Suspense>
          } />
          <Route path="/events/year-end-festa" element={
            <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0c2a8a' }} />}>
              <YearEndFestaEventPage />
            </React.Suspense>
          } />
          <Route path="/events/group-50" element={
            <Group50Route>
              <React.Suspense fallback={<div className="grp50-boot" style={{ minHeight: '100vh', background: '#fff' }} />}>
                <Group50EventPage />
              </React.Suspense>
            </Group50Route>
          } />
          <Route path="/events/pro-50" element={
            <React.Suspense fallback={<div className="grp50-boot" style={{ minHeight: '100vh', background: '#fff' }} />}>
              <Pro50EventPage />
            </React.Suspense>
          } />
          <Route path="/events/pro-ai" element={
            <React.Suspense fallback={<div className="proai-boot" style={{ minHeight: '100vh', background: '#fff' }} />}>
              <ProAiEventPage />
            </React.Suspense>
          } />
          <Route path="/events/scoring" element={
            <React.Suspense fallback={<div className="scoring-lp-boot" style={{ minHeight: '100vh', background: '#fff' }} />}>
              <ScoringEventPage />
            </React.Suspense>
          } />
          {/* 구 /events/hwp 는 쿼리를 유지한 채 시험지 랜딩으로 보낸다. 이어하기 페이지는 폐기됐다. */}
          <Route path="/events/hwp" element={<Navigate to={`/events/hwp-exam${window.location.search}`} replace />} />
          <Route path="/events/hwp/continue" element={<Navigate to={`/events/hwp-exam${window.location.search}`} replace />} />
          {HWP_DOC_EVENT_ROUTES.map(([routePath, docType]) => (
            <Route key={routePath} path={routePath} element={
              <React.Suspense fallback={<HwpDocBootFallback />}>
                <HwpDocEventPage type={docType} />
              </React.Suspense>
            } />
          ))}
          
          {/* 🔒 개인 페이지들 (로그인 필수) */}
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/subscription/pro" element={<ProSubscription />} />
          <Route path="/subscription/documents/:paymentKey" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <PaymentDocumentsPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/subscription/billing-key" element={
            <PrivateRoute>
              <BillingKeyRegistration />
            </PrivateRoute>
          } />
          <Route path="/billing/success" element={
            <PrivateRoute>
              <BillingSuccess />
            </PrivateRoute>
          } />
          <Route path="/billing/fail" element={
            <PrivateRoute>
              <BillingFail />
            </PrivateRoute>
          } />
          <Route path="/payment/receipt/:paymentId" element={
            <PrivateRoute>
              <PaymentReceipt />
            </PrivateRoute>
          } />
          <Route path="/refund/receipt/:paymentKey" element={
            <PrivateRoute>
              <RefundReceipt />
            </PrivateRoute>
          } />
          <Route path="/refund/receipt" element={
            <PrivateRoute>
              <RefundReceipt />
            </PrivateRoute>
          } />
          <Route path="/payment/success" element={
            <PrivateRoute>
              <PaymentSuccess />
            </PrivateRoute>
          } />
          <Route path="/payment/widget-success" element={
            <PrivateRoute>
              <PaymentWidgetSuccess />
            </PrivateRoute>
          } />
          <Route path="/payment/widget-fail" element={
            <PrivateRoute>
              <PaymentWidgetFail />
            </PrivateRoute>
          } />
          <Route path="/profile" element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          } />

          {/* 신규 친구 초대 시스템 — 운영에서는 명시적 feature flag로 공개 */}
          <Route path="/invite/welcome" element={<InviteWelcomePage />} />
          <Route path="/invite" element={<InviteFeatureRoute />} />

          {/* 연수 파트너 안내·조건 확인·투명 정산 */}
          <Route path="/partner" element={
            <React.Suspense fallback={<PartnerPageFallback />}>
              <PartnerProgramPage />
            </React.Suspense>
          } />
          <Route path="/partner/present" element={
            <React.Suspense fallback={<PartnerPageFallback />}>
              <PartnerPresentationPage />
            </React.Suspense>
          } />
          <Route path="/partner/contract" element={
            <React.Suspense fallback={<PartnerPageFallback />}>
              <PartnerContractDraftPage />
            </React.Suspense>
          } />
          <Route path="/partner/contract-draft" element={<Navigate to="/partner/contract" replace />} />
          <Route path="/partner/agree" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <PartnerAgreementPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/partner/dashboard" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <PartnerDashboardPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          
          {/* 👨‍💼 관리자 시스템 페이지들 (관리자 권한 필수) */}
          <Route path="/admin" element={
            <PrivateRoute>
              <AdminHubPage />
            </PrivateRoute>
          } />
          <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/operations" element={
            <PrivateRoute>
              <AdminOperationsPage />
            </PrivateRoute>
          } />
          <Route path="/admin/invites" element={
            <PrivateRoute>
              <InviteOperationsPage />
            </PrivateRoute>
          } />
          <Route path="/admin/trainings" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <TrainingAdminPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/admin/training-orders" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <TrainingOrdersAdminPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/admin/partner-settlements" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <PartnerSettlementAdminPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/admin/training-guide" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <TrainingOperationsGuidePage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/my/trainings" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <MyTrainingsPage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/my/trainings/:slug/documents" element={
            <PrivateRoute>
              <React.Suspense fallback={<PartnerPageFallback />}>
                <TrainingEvidencePage />
              </React.Suspense>
            </PrivateRoute>
          } />
          <Route path="/admin/refunds" element={
            <PrivateRoute>
              <RefundConsolePage />
            </PrivateRoute>
          } />
          <Route path="/admin/coupon-manager" element={
            <PrivateRoute>
              <CouponManager />
            </PrivateRoute>
          } />
          <Route path="/admin/school-trial" element={
            <PrivateRoute>
              <SchoolTrialReviewPage />
            </PrivateRoute>
          } />
          <Route path="/admin/organizations" element={
            <PrivateRoute>
              <OrganizationAdminPage />
            </PrivateRoute>
          } />
          <Route path="/admin/bulk-purchases" element={
            <PrivateRoute>
              <BulkPurchaseAdminPage />
            </PrivateRoute>
          } />
          <Route path="/admin/pilot-contest" element={
            <PrivateRoute>
              <PilotContestManager />
            </PrivateRoute>
          } />
          <Route path="/admin/pilot" element={<Navigate to="/admin/pilot/delivery" replace />} />
          <Route path="/admin/pilot/delivery" element={
            <PrivateRoute>
              <RiseAdminDelivery />
            </PrivateRoute>
          } />
          <Route path="/admin/pilot/survey" element={
            <PrivateRoute>
              <RiseAdminSurvey />
            </PrivateRoute>
          } />
          <Route path="/admin/pilot/coupon" element={
            <PrivateRoute>
              <RiseAdminCoupon />
            </PrivateRoute>
          } />
          <Route path="/admin/pilot/users" element={
            <PrivateRoute>
              <RiseAdminUsers />
            </PrivateRoute>
          } />
          <Route path="/admin/analytics" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/content" element={
            <PrivateRoute>
              <ContentManager />
            </PrivateRoute>
          } />
          <Route path="/admin/ir" element={
            <PrivateRoute>
              <IrDashboard />
            </PrivateRoute>
          } />
          <Route path="/admin/roas" element={
            <PrivateRoute>
              <RoasDashboard />
            </PrivateRoute>
          } />
          <Route path="/admin/usage" element={
            <PrivateRoute>
              <UsageDashboard />
            </PrivateRoute>
          } />
          <Route path="/admin/revenue-funnel" element={
            <PrivateRoute>
              <RevenueFunnelPage />
            </PrivateRoute>
          } />
          <Route path="/admin/lead-sender" element={
            <PrivateRoute>
              <LeadSender />
            </PrivateRoute>
          } />
          <Route path="/admin/leads" element={
            <PrivateRoute>
              <CustomerLeadsPage />
            </PrivateRoute>
          } />
          <Route path="/admin/pricing-policy" element={
            <PrivateRoute>
              <AdminPricingPolicyPage />
            </PrivateRoute>
          } />
          <Route path="/admin/exhibition-registrations" element={<Navigate to="/admin/leads" replace />} />
          
          {/* 고객센터 페이지들 - 공개 접근 가능 (구글 스타일) */}
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/help/notice/:noticeId" element={<HelpCenterPage />} />
          
          {/* 지원 관련 페이지들 - 공개 접근 가능 (기존 호환성 유지) */}
          <Route path="/support" element={<SupportPage />} />
          <Route path="/support/faq" element={<Navigate to="/support" replace />} />
          <Route path="/support/faq/:id" element={<FaqDetailPage />} />
          <Route path="/support/getting-started" element={<GettingStartedPage />} />
          <Route path="/support/worksheet" element={<WorksheetPage />} />
          <Route path="/support/features" element={<FeaturesPage />} />
          <Route path="/support/payment" element={<PaymentPage />} />
          <Route path="/support/technical" element={<TechnicalPage />} />
          
          {/* 공개 페이지들 */}
          {/* 현재 사용하지 않음 - 오류 발생으로 비활성화 */}
          {/* <Route path="/share" element={<QuizResult isSharedView={true} />} /> */}
          <Route path="/team" element={<TeamPage />} />
          <Route path="/company-info" element={<CompanyInfo />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
          {/* Google Play / App Store 계정 삭제 정책 대응 — 비로그인도 접근 가능 */}
          <Route path="/account/delete" element={<AccountDeletePage />} />
          
          {/* 기존 라우트들 (호환성 유지) */}
          <Route path="/login" element={<Navigate to="/auth" state={{ mode: 'login' }} replace />} />
          <Route path="/register" element={<Navigate to="/auth" state={{ mode: 'signup' }} replace />} />
          <Route path="/reset-password" element={<Navigate to="/auth" state={{ mode: 'login' }} replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ErrorBoundary>
        </PageTransition>
      </div>
    </div>
  );
};

// 공지 모달 조건부 렌더링 컴포넌트
const ConditionalAnnouncementModal = () => {
  const { pathname } = useLocation();
  // 기간·경로(전역 숨김 경로 포함) 필터는 config/announcements.js, 브라우저 숨김 기록은 팝업이 확인한다.
  const announcements = React.useMemo(() => getVisibleAnnouncements(pathname), [pathname]);

  if (announcements.length === 0) {
    return null;
  }
  return <AnnouncementModal announcements={announcements} />;
};

// 새 소식(업데이트 카드뉴스) — 조건은 config/whatsNew.js, 공지 팝업과 겹치지 않게 popupGate가 조율한다.
const WhatsNewHost = () => {
  const { pathname } = useLocation();
  return <WhatsNewModal pathname={pathname} />;
};

// SEO 노출 방지 컴포넌트 (Private 페이지용)
const PrivatePageSEO = () => {
  const location = useLocation();
  
  // /private로 시작하는 경로에서만 noindex 처리 및 메타 태그 설정
  if (location.pathname.startsWith('/private/daycare-evaluation')) {
    // 경로별 타이틀 설정
    let pageTitle = '온종일돌봄시설 근무성적평정 시스템';
    
    if (location.pathname.includes('/access')) {
      pageTitle = '로그인 | 온종일돌봄시설 근무성적평정 시스템';
    } else if (location.pathname.includes('/dashboard')) {
      pageTitle = '대시보드 | 온종일돌봄시설 근무성적평정';
    } else if (location.pathname.includes('/self-evaluation')) {
      pageTitle = '자기평가 | 온종일돌봄시설 근무성적평정';
    } else if (location.pathname.includes('/supervisor-evaluation')) {
      pageTitle = '상급자 평가 | 온종일돌봄시설 근무성적평정';
    } else if (location.pathname.includes('/multi-evaluation')) {
      pageTitle = '다면평가 | 온종일돌봄시설 근무성적평정';
    } else if (location.pathname.includes('/admin')) {
      pageTitle = '관리자 | 온종일돌봄시설 근무성적평정';
    } else if (location.pathname.includes('/result')) {
      pageTitle = '평가 결과 | 온종일돌봄시설 근무성적평정';
    } else if (location.pathname.includes('/employee')) {
      pageTitle = '직원 접근 | 온종일돌봄시설 근무성적평정';
    }
    
    return (
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="온종일돌봄시설 종사자 근무성적평정 시스템입니다. 접근 권한이 필요합니다." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  if (location.pathname.startsWith('/private/watkids-eval')) {
    return (
      <Helmet>
        <title>2026 근무성적평정 | 노원구온종일돌봄사업단</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
    );
  }

  if (location.pathname.startsWith('/pilot/rise')) {
    return (
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }
  
  return null;
};

// 메인 앱 컴포넌트
function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <ChatProvider>
          <WorksheetProvider>
            <MathJaxProvider>
              <HelmetProvider>
                <LayerProvider>
                <ToastProvider>
                <ConfirmDialogProvider>
                <Router>
                  <KakaoBrowserDetect />
                  <ScrollToTop />
                  <RouteToastCleaner />
                  {/* 친구 초대 first-touch 수집 + 가입 완료 후 서버 귀속 */}
                  <InviteAttributionBridge />
                  {/* 🔒 Private 페이지 SEO 노출 방지 */}
                  <PrivatePageSEO />
                  {/* 🎁 회원가입 직후 7일 무료 체험 자동 시작 트리거 */}
                  <FreeTrialAutoStarter />
                  <Ai30ArrivalTracker />
                  {/* 🎉 학급사진 유입 가입자는 원래 화면에서 PRO 시작을 명확히 확인 */}
                  <ClassPhotoTrialWelcome />
                  {/* 🎉 공지 모달 - 활성화된 공지가 있으면 자동 표시 (특정 경로 제외) */}
                  <ConditionalAnnouncementModal />
                  <WhatsNewHost />
                  <RouteChangeListener>
                    <AppLayout />
                  </RouteChangeListener>
                </Router>
                </ConfirmDialogProvider>
                </ToastProvider>
                </LayerProvider>
              </HelmetProvider>
            </MathJaxProvider>
          </WorksheetProvider>
        </ChatProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App; 